import { apiClient } from './apiClient';

/**
 * A user's OWN external credentials — not the workspace's.
 *
 * Teamwork attributes a task to whoever owns the API key used to create it,
 * so without a personal key every support ticket you file lands in Teamwork
 * authored by a shared service account.
 *
 * The key is never returned by the API. `configured` plus the resolved
 * Teamwork account is all the UI ever sees.
 */
export interface TeamworkKeyStatus {
  configured: boolean;
  metadata?: {
    teamwork_user_id?: string;
    teamwork_name?: string;
    teamwork_email?: string;
  };
  updated_at?: string | null;
  /** True when the caller is AgileCo staff. Only internal users have a
   *  Teamwork account to authenticate as; a client's tickets are filed with
   *  Core's External Core Support credential and they are never asked. */
  internal?: boolean;
}

export const userIntegrationsService = {
  async getTeamwork(): Promise<TeamworkKeyStatus> {
    const { data } = await apiClient.get<TeamworkKeyStatus>(
      '/account/integrations/teamwork',
    );
    return data;
  },

  /** Validated server-side against Teamwork before it is stored, so a bad key
   *  is rejected here rather than silently falling back forever. */
  async setTeamwork(apiKey: string): Promise<TeamworkKeyStatus> {
    const { data } = await apiClient.put<TeamworkKeyStatus>(
      '/account/integrations/teamwork',
      { api_key: apiKey },
    );
    return data;
  },

  async removeTeamwork(): Promise<TeamworkKeyStatus> {
    const { data } = await apiClient.delete<TeamworkKeyStatus>(
      '/account/integrations/teamwork',
    );
    return data;
  },
};
